import { uiText } from "@/lib/i18n/uiText";
import { useMemo, useState } from "react";
import { useParams, Navigate } from "@/lib/router-compat";
import { Loader2 } from "lucide-react";
import { PredictionCard } from "@/components/predictions/PredictionCard";
import { PredictionDetailModal } from "@/components/predictions/PredictionDetailModal";
import { DataPagination } from "@/components/shared/DataPagination";
import { InternalLinks } from "@/components/seo/InternalLinks";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useLandingPredictions } from "@/hooks/useLandingPredictions";
import { findLanding, landingWindow, LANDING_ALIASES } from "@/lib/predictionLandings";
import { useSEO } from "@/lib/useSEO";
import { breadcrumbJsonLd } from "@/lib/seoSchema";
import { clusterFaqJsonLd, mergeClusterFaqs } from "@/lib/clusterFaqs";
import { SITE_URL } from "@/lib/entities";
import type { Prediction } from "@/types";

/**
 * /predictions/:landing — time-based and market×time SEO landing pages
 * ("bonus picks today", "correct score prediction today", "predictions
 * tomorrow"). Data-driven from the PREDICTION_LANDINGS registry.
 */
export default function PredictionLanding() {
  const { landing: slug = "" } = useParams();
  const aliasTarget = LANDING_ALIASES[slug];
  const landing = findLanding(slug);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(12);
  const [selected, setSelected] = useState<Prediction | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const window = useMemo(
    () => (landing ? landingWindow(landing.timeframe) : { from: undefined, to: undefined }),
    [landing],
  );

  const { data, isLoading, isFetching } = useLandingPredictions({
    from: window.from!,
    to: window.to!,
    marketMatchers: landing?.marketMatchers!,
    leagueMatchers: landing?.leagueMatchers!,
    minConfidence: landing?.minConfidence!,
    sort: landing?.sort!,
    page,
    pageSize,
  });

  const canonicalPath = `/predictions/${slug}`;
  // Google requires at least two Q&A pairs for FAQ rich results, and the schema
  // must mirror the visible copy — top up curated FAQs with the shared defaults.
  const faqs = useMemo(
    () =>
      landing
        ? mergeClusterFaqs(landing.faqs, { kind: "league", heading: landing.h1 })
        : [],
    [landing],
  );
  const jsonLd = landing
    ? [
        {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: landing.h1,
          description: landing.description,
          url: `${SITE_URL}${canonicalPath}`,
        },
        breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Predictions", path: "/predictions" },
          { name: landing.label, path: canonicalPath },
        ]),
        ...(faqs.length ? [clusterFaqJsonLd(faqs)] : []),
      ]
    : undefined;

  useSEO({
    title: landing?.title ?? "Football Predictions",
    description: landing?.description!,
    ogTitle: landing?.ogTitle!,
    ogDescription: landing?.ogDescription!,
    canonicalPath,
    keywords: landing?.keywords!,
    jsonLd: jsonLd!,
  });


  if (aliasTarget) return <Navigate to={`/predictions/${aliasTarget}`} replace />;
  if (!landing) return <Navigate to="/predictions" replace />;

  const rows = data?.rows ?? [];
  const totalCount = data?.count ?? 0;

  return (
    <div className="space-y-8 animate-fade-in">
      <Breadcrumbs
        items={[
          { name: "Home", path: "/" },
          { name: "Predictions", path: "/predictions" },
          { name: landing.label, path: canonicalPath },
        ]}
      />

      <header className="space-y-2">
        <h1 className="text-2xl sm:text-3xl font-bold">{landing.h1}</h1>
        <p className="text-muted-foreground max-w-3xl">{landing.intro}</p>
      </header>

      <section aria-label={uiText("Predictions")} className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="h-5 w-5 mr-2 animate-spin" />  {uiText("Loading predictions…")} </div>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground">
             {uiText("No matching predictions are available right now. Check back closer to kick-off or explore related predictions below.")} </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {rows.map((p) => (
                <PredictionCard
                  key={p.id}
                  prediction={p}
                  onClick={() => {
                    setSelected(p);
                    setModalOpen(true);
                  }}
                />
              ))}
            </div>
            <DataPagination
              page={page}
              pageSize={pageSize}
              totalCount={totalCount}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              pageSizeOptions={[12, 24, 48]}
              isLoading={isFetching}
            />
          </>
        )}
      </section>

      {landing.sections?.map((section) => (
        <section key={section.heading} className="max-w-3xl space-y-3">
          <h2 className="text-2xl font-bold">{uiText(section.heading)}</h2>
          {section.paragraphs.map((para, i) => (
            <p key={i} className="text-muted-foreground leading-relaxed">
              {para}
            </p>
          ))}
          {section.bullets && (
            <ul className="list-disc space-y-2 pl-5 text-muted-foreground leading-relaxed">
              {section.bullets.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          )}
        </section>
      ))}

      {faqs.length > 0 && (
        <section className="max-w-3xl space-y-4">
          <h2 className="text-2xl font-bold">{uiText("Frequently Asked Questions")}</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-left">{uiText(f.question)}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  {uiText(f.answer)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}

      <InternalLinks />

      <PredictionDetailModal prediction={selected} open={modalOpen} onOpenChange={setModalOpen} />
    </div>
  );
}
